import { servicesOrchestratorValues } from "@/config/services";
import logger from "@/lib/logging/logger";
import { type NativeCameraFacing, sendOutboundNativeMessage } from "@/lib/native-bridge";
import type { CameraFacingMode } from "@/services/types";
import { nativeCamera } from "./nativeCamera";

export type CameraProvider = "browser" | "native";

const DEBUG_STORAGE_KEY = "pandas-camera-debug";
const MOCK_STORAGE_KEY = "pandas-mock-native";
// The mock fabricates stills that take the normal upload and grading path, so it exists only
// where no real customer session runs.
const MOCK_ALLOWED = !servicesOrchestratorValues.isProduction && !servicesOrchestratorValues.isUat;

/** The wrapper advertises `camera` unless its managed config sets `cameraMode = web`. */
export function hasNativeCameraCapability(): boolean {
	if (typeof window === "undefined") return false;
	const capabilities: unknown = window.PandasNative?.capabilities;
	return Array.isArray(capabilities) && capabilities.includes("camera");
}

export function getCameraProvider(): CameraProvider {
	return hasNativeCameraCapability() ? "native" : "browser";
}

export function toNativeFacing(facing: CameraFacingMode): NativeCameraFacing {
	return facing === "user" ? "front" : "back";
}

function readSwitch(key: string): string | null {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

// The router rewrites the search params before any camera page mounts, so the test switches
// are read off the boot URL once and kept for the session.
function persistSwitch(params: URLSearchParams, param: string, key: string): void {
	const value = params.get(param);
	if (value === null) return;
	try {
		if (value === "" || value === "0" || value === "off") window.sessionStorage.removeItem(key);
		else window.sessionStorage.setItem(key, value);
	} catch {
		// Storage is unavailable (private mode): the switch cannot be kept, so it stays off.
	}
}

export function isCameraDebugEnabled(): boolean {
	return readSwitch(DEBUG_STORAGE_KEY) === "1";
}

function startCameraRuntime(): void {
	nativeCamera.listen();
	// A reload leaves the wrapper's camera and torch running with no page to stop them, and the
	// wrapper denies getUserMedia for as long as its own camera runs.
	if (hasNativeCameraCapability()) sendOutboundNativeMessage("camera.stop", {});
	document.documentElement.dataset.cameraProvider = getCameraProvider();
}

/**
 * `?cameraDebug=1` shows the provider badge; `?mockNative=camera` fakes the wrapper in a desktop
 * browser, outside production and UAT only. A promise comes back only while the mock is loading,
 * so a normal boot stays synchronous.
 */
export function initCameraRuntime(): Promise<void> | undefined {
	const params = new URLSearchParams(window.location.search);
	persistSwitch(params, "cameraDebug", DEBUG_STORAGE_KEY);
	if (MOCK_ALLOWED) persistSwitch(params, "mockNative", MOCK_STORAGE_KEY);
	if (MOCK_ALLOWED && readSwitch(MOCK_STORAGE_KEY) === "camera" && !window.PandasNative) {
		return import("./mockNativeBridge")
			.then(({ installMockNativeBridge }) => installMockNativeBridge())
			.catch((error: unknown) => logger.warning("INFO", "Native camera mock failed to load", error))
			.then(startCameraRuntime);
	}
	startCameraRuntime();
	return undefined;
}
